// Ops → Automation → Library: the reusable, define-once/reference-everywhere metadata that flows share
// (Connections, Capabilities, Policies, AI profiles, Validation tools, Domain tables). Schema-driven
// CRUD over services/ecapApi.* with where-used and an in-use delete guard (409). Domain tables get a
// dedicated columns/rows/CSV editor. See src/pages/README.md.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Alert } from '../ui/primitives';
import { IconTrash } from '../ui/icons';
import {
  connections, capabilities, policies, aiProfiles, validationTools, domainTables, importDomainTableCsv,
  getPremiumMarks, setPremiumMark, whoami,
  listCodeCapabilities, getCodeCapability, invokeCodeCapability,
  createCodeCapability, saveCodeCapability, publishCodeCapability, testCodeCapability, assistCodeCapability,
  deleteCodeCapability,
} from '../services/ecapApi';

// F3: NUMBER_RANGE (param e.g. "1..60") and DATE_FUTURE (no param) are backend-supported Rule types that had
// no console control anywhere — now authorable as reusable policies here and attachable via PolicyRuleEditor.
// B3: COMPARE is the cross-field rule — param "<op> <otherField>", e.g. "> pickupTime" on returnPickupTime.
const RULE_TYPES = ['EMAIL', 'EMAIL_MX', 'PHONE', 'REGEX', 'MIN_LENGTH', 'MAX_LENGTH', 'NUMBER_RANGE', 'DATE_FUTURE', 'TOOL', 'LOOKUP_EXISTS', 'COMBINATION', 'COMPARE'];
const inp = 'w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm';
// Library tab key → the premium item type. Every library type is monetizable (per company). Drives the badge/toggle.
const PREMIUM_TYPE = { connections: 'connection', capabilities: 'capability', policies: 'policy', 'ai-profiles': 'aiProfile', 'validation-tools': 'validationTool', 'domain-tables': 'domainTable' };

// Field schema per library type. t: text|textarea|number|select|list|map
const LIBS = [
  { key: 'connections', label: 'Connections', api: connections, hint: 'Reusable endpoint + auth for hooks & tools',
    fields: [{ n: 'name', t: 'text' }, { n: 'baseUrl', t: 'text', ph: 'https://api.example.com' },
      { n: 'authType', t: 'select', opts: ['NONE', 'API_KEY', 'BEARER', 'BASIC', 'OAUTH2'] }, // OPSLIB-06: BASIC was missing
      { n: 'authHeaderName', t: 'text', ph: 'x-api-key', showIf: (d) => d.authType === 'API_KEY' },
      { n: 'authValue', t: 'password', ph: 'API key / token, or a secret reference', showIf: (d) => d.authType === 'API_KEY' || d.authType === 'BEARER' },
      // OPSLIB-06: HTTP Basic auth credentials (were unauthorable — a BASIC connection couldn't be created/edited).
      { n: 'username', t: 'text', ph: 'basic-auth username', showIf: (d) => d.authType === 'BASIC' },
      { n: 'password', t: 'password', ph: 'basic-auth password, or a secret reference', showIf: (d) => d.authType === 'BASIC' },
      // OAUTH2 client-credentials — the runtime fetches & caches a bearer from the token endpoint.
      { n: 'tokenUrl', t: 'text', ph: 'https://issuer/oauth/token', showIf: (d) => d.authType === 'OAUTH2' },
      { n: 'clientId', t: 'text', showIf: (d) => d.authType === 'OAUTH2' },
      { n: 'clientSecret', t: 'password', ph: 'client secret, or a secret reference', showIf: (d) => d.authType === 'OAUTH2' },
      { n: 'scope', t: 'text', ph: 'optional scope', showIf: (d) => d.authType === 'OAUTH2' },
      { n: 'headers', t: 'map' }],
    blank: { authType: 'NONE', headers: {} } },
  { key: 'capabilities', label: 'Capabilities', api: capabilities, hint: 'Reusable named action (Quote, Booking)',
    fields: [{ n: 'name', t: 'text' }, { n: 'connectionRef', t: 'ref', ref: 'connections' }, { n: 'method', t: 'select', opts: ['POST', 'PUT', 'GET'] },
      { n: 'url', t: 'text' }, { n: 'bodyTemplate', t: 'textarea' }, { n: 'responseMapping', t: 'map' },
      { n: 'trigger', t: 'select', opts: ['ON_COMPLETE', 'ON_ATTRIBUTES'] }, { n: 'triggerAttributes', t: 'list' }, { n: 'messageTemplate', t: 'text' }],
    blank: { method: 'POST', trigger: 'ON_COMPLETE', responseMapping: {}, triggerAttributes: [] } },
  { key: 'policies', label: 'Policies', api: policies, hint: 'Reusable validation referenced by attributes',
    fields: [{ n: 'name', t: 'text' }, { n: 'type', t: 'select', opts: RULE_TYPES }, { n: 'param', t: 'text' },
      { n: 'message', t: 'text' }, { n: 'severity', t: 'select', opts: ['BLOCK', 'WARN'] }],
    blank: { type: 'EMAIL', severity: 'BLOCK' } },
  { key: 'ai-profiles', label: 'AI profiles', api: aiProfiles, hint: 'Reusable per-attribute AI instructions',
    fields: [{ n: 'name', t: 'text' }, { n: 'extractionHint', t: 'textarea' }, { n: 'examples', t: 'list' },
      { n: 'aliases', t: 'list' }, { n: 'negativeExamples', t: 'list' }],
    blank: { examples: [], aliases: [], negativeExamples: [] } },
  { key: 'validation-tools', label: 'Validation tools', api: validationTools, hint: 'External API validators (Connection-backed)',
    fields: [{ n: 'name', t: 'text' }, { n: 'connectionRef', t: 'ref', ref: 'connections' }, { n: 'method', t: 'select', opts: ['POST', 'GET', 'PUT'] },
      { n: 'url', t: 'text' }, { n: 'bodyTemplate', t: 'textarea' }, { n: 'resultPath', t: 'text', ph: 'json.valid' }, { n: 'expectedValue', t: 'text' }],
    blank: { method: 'POST' } },
  { key: 'domain-tables', label: 'Domain tables', api: domainTables, hint: 'Reference datasets (Cities, Routes) for lookups', domain: true },
];

function OpsLibrary({ authSession }) {
  const [tab, setTab] = useState(LIBS[0].key);
  const lib = LIBS.find((l) => l.key === tab);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null); // {..item, __new?}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); // OPSLIB-DOUBLE-SUBMIT: guards the editor Save button
  const loadSeq = useRef(0); // OPSLIB-LOAD-RACE: monotonic token so a stale tab's list can't overwrite a newer one
  const [refLists, setRefLists] = useState({ connections: [] }); // for ref dropdowns
  const [marks, setMarks] = useState([]); // this company's premium refs (type:id) — super-admin registry marks
  const [isAdmin, setIsAdmin] = useState(false);
  const premiumType = PREMIUM_TYPE[tab]; // undefined for connections

  const load = useCallback(async () => {
    if (!lib) { setLoading(false); return; } // 'code' tab renders its own panel below
    // OPSLIB-LOAD-RACE: on a fast tab switch two list() calls are in flight; without an order guard the
    // SLOWER one can resolve last and paint the wrong tab's items. Stamp each call and only the latest
    // one is allowed to commit its result (mirrors OpsFlows.selectFlow's latestSelectRef guard).
    const seq = ++loadSeq.current;
    setLoading(true); setError(null); setDraft(null);
    try { const data = await lib.api.list(authSession) || []; if (seq === loadSeq.current) setItems(data); }
    catch (e) { if (seq === loadSeq.current) setError(e.message); }
    finally { if (seq === loadSeq.current) setLoading(false); }
  }, [lib, authSession]);

  const loadMarks = useCallback(() => {
    getPremiumMarks(authSession).then((d) => setMarks(d?.premium || [])).catch(() => setMarks([]));
  }, [authSession]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMarks(); whoami(authSession).then((w) => setIsAdmin(!!w?.admin)).catch(() => setIsAdmin(false)); }, [loadMarks, authSession]);
  // Connections power the connectionRef dropdowns on capabilities & validation tools.
  useEffect(() => {
    connections.list(authSession).then((c) => setRefLists({ connections: c || [] })).catch(() => {});
  }, [authSession]);

  // Toggle an item's premium flag (super-admin). Optimistic; reloads marks on error.
  const togglePremium = async (id, next) => {
    const ref = `${premiumType}:${id}`;
    setMarks((m) => next ? [...m, ref] : m.filter((r) => r !== ref));
    try { await setPremiumMark(premiumType, id, next, authSession); }
    catch (e) { setError(e.message); loadMarks(); }
  };

  const startNew = () => setDraft(lib.domain ? { id: '', name: '', columns: [], rows: [], keyColumns: [], __new: true } : { id: '', ...(lib.blank || {}), __new: true });
  const edit = (it) => setDraft(JSON.parse(JSON.stringify({ ...it, __new: false })));

  const save = async () => {
    if (!draft.id?.trim()) { setError('id is required'); return; }
    // OPSLIB-DOUBLE-SUBMIT: disable Save while in flight so a double-click can't fire two PUTs (the second
    // racing the load()/setDraft(null) that follows the first).
    if (saving) return;
    setSaving(true); setError(null);
    try { const { __new, ...body } = draft; await lib.api.save(draft.id, body, authSession); await load(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await lib.api.remove(id, authSession); await load(); }
    // OPSLIB-409: branch on the numeric HTTP status, not a substring of the message. The backend sends the
    // in-use guard as a 409 with a `{error}` JSON body, so the human-readable message no longer contains
    // "409" — the old `e.message.includes('409')` never matched and the raw backend error showed instead.
    catch (e) { setError(e.status === 409 || (e.message || '').toLowerCase().includes('in use') ? 'In use by a flow — remove references first.' : e.message); }
  };

  const tabBar = (
    <div className="flex gap-1.5 flex-wrap mb-4">
      {LIBS.map((l) => (
        <button key={l.key} onClick={() => setTab(l.key)}
          className={`px-3 py-1.5 rounded-lg text-sm ${tab === l.key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{l.label}</button>
      ))}
      <button onClick={() => setTab('code')}
        className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'code' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Code capabilities</button>
    </div>
  );

  if (tab === 'code') {
    return <div>{tabBar}<CodeCapabilitiesPanel authSession={authSession} isAdmin={isAdmin} /></div>;
  }

  return (
    <div>
      {tabBar}
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      <div className="text-xs text-slate-500 mb-3">{lib.hint}</div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <div>
          <Button size="sm" onClick={startNew} className="mb-2">+ New {lib.label.replace(/s$/, '').toLowerCase()}</Button>
          {loading && <div className="text-sm text-slate-400">Loading…</div>}
          <div className="space-y-1.5">
            {items.map((it) => {
              const isPremium = premiumType && marks.includes(`${premiumType}:${it.id}`);
              return (
              <div key={it.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${isPremium ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-white'}`}>
                <button className="flex-1 min-w-0 text-left" onClick={() => edit(it)}>
                  <div className="font-medium text-slate-800 text-sm truncate flex items-center gap-1.5">
                    {it.id}
                    {isPremium && <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-700 shrink-0">★ premium</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{it.name || it.type || it.baseUrl || ''}</div>
                </button>
                {/* Super-admin flags an item premium FOR THIS COMPANY (monetization). Non-admins just see the badge. */}
                {premiumType && isAdmin && (
                  <button className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${isPremium ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400 hover:text-amber-600'}`}
                    title={isPremium ? 'Premium — click to unmark' : 'Mark premium'} onClick={() => togglePremium(it.id, !isPremium)}>★</button>
                )}
                <button className="shrink-0 text-rose-500 hover:text-rose-600 p-1 -mr-1" onClick={() => remove(it.id)} aria-label="Delete"><IconTrash size={15} /></button>
              </div>
              );
            })}
            {!loading && items.length === 0 && <div className="text-sm text-slate-400">None yet.</div>}
          </div>
        </div>

        <div>
          {!draft && <div className="text-sm text-slate-400 mt-1">Select an item to edit, or create one.</div>}
          {draft && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <Lbl t="id"><input className={inp} value={draft.id} disabled={!draft.__new} onChange={(e) => setDraft({ ...draft, id: e.target.value.trim() })} /></Lbl>
              {lib.domain ? <DomainEditor draft={draft} setDraft={setDraft} authSession={authSession} onImported={load} onError={setError} />
                : lib.fields.filter((f) => !f.showIf || f.showIf(draft)).map((f) => (
                  <FieldInput key={f.n} f={f} value={draft[f.n]} refLists={refLists}
                    onChange={(v) => setDraft({ ...draft, [f.n]: v })} />
                ))}
              <div className="flex gap-2 mt-4"><Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button><Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button></div>
              {!draft.__new && (lib.key === 'connections' || lib.key === 'validation-tools') && (
                <TestPanel lib={lib} id={draft.id} authSession={authSession} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Lbl({ t, children }) { return <label className="block mb-2.5"><span className="block text-xs text-slate-500 mb-1">{t}</span>{children}</label>; }

// OPSLIB-07: a key=value map editor. The displayed text is a LOCAL buffer, decoupled from the parsed object,
// so a half-typed line (a key before its '=') survives instead of being dropped when the parsed value
// round-trips back through the parent (which would otherwise re-serialize to '' and erase the in-progress key).
const serializeMap = (o) => Object.entries(o || {}).map(([k, v]) => `${k}=${v}`).join('\n');
const parseMap = (t) => { const o = {}; (t || '').split('\n').forEach((l) => { const i = l.indexOf('='); if (i > 0) o[l.slice(0, i).trim()] = l.slice(i + 1).trim(); }); return o; };
function MapField({ label, value, onChange }) {
  const [text, setText] = useState(() => serializeMap(value));
  const lastEmitted = useRef(serializeMap(value)); // the serialization we last pushed up — to detect EXTERNAL changes
  useEffect(() => {
    const s = serializeMap(value);
    if (s !== lastEmitted.current) { lastEmitted.current = s; setText(s); } // value changed from outside → resync
  }, [value]);
  return <Lbl t={`${label} (key=value per line)`}><textarea className={inp} rows={2} value={text}
    onChange={(e) => { setText(e.target.value); const o = parseMap(e.target.value); lastEmitted.current = serializeMap(o); onChange(o); }} /></Lbl>;
}

// "Test" for connections (probe/mint token) and validation tools (run against a sample value).
function TestPanel({ lib, id, authSession }) {
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = lib.key === 'validation-tools'
        ? await lib.api.testValue(id, value, authSession)
        : await lib.api.test(id, authSession);
      setResult(r);
    } catch (e) { setResult({ ok: false, error: e.message }); }
    finally { setBusy(false); }
  };
  const ok = result && (result.ok === true || result.valid === true);
  const bad = result && (result.ok === false || result.valid === false || result.error);
  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Test</div>
      <div className="flex gap-2 items-center">
        {lib.key === 'validation-tools' && (
          <input className={inp} style={{ maxWidth: 220 }} placeholder="sample value (e.g. 4155552671)"
            value={value} onChange={(e) => setValue(e.target.value)} />
        )}
        <Button size="sm" variant="outline" onClick={run} disabled={busy}>{busy ? 'Testing…' : (lib.key === 'validation-tools' ? 'Run' : 'Test connection')}</Button>
      </div>
      {result && (
        <div className={`mt-2 text-sm ${ok ? 'text-emerald-700' : bad ? 'text-rose-600' : 'text-slate-600'}`}>
          {ok ? '✓ ' : bad ? '✗ ' : ''}{result.detail || result.note || result.error || (result.valid != null ? `valid: ${result.valid}` : JSON.stringify(result))}
        </div>
      )}
    </div>
  );
}

function FieldInput({ f, value, onChange, refLists }) {
  const label = f.n.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  if (f.t === 'ref') {
    const opts = (refLists?.[f.ref] || []);
    const known = !value || opts.some((o) => o.id === value);
    return <Lbl t={label}><select className={inp} value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— none —</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.name ? `${o.name} (${o.id})` : o.id}</option>)}
      {!known && <option value={value}>{value} (not in library)</option>}
    </select></Lbl>;
  }
  // OPSLIB-06: render a fallback <option> for a stored value not in opts, so an unknown/newer enum (e.g. a
  // BASIC authType) isn't silently coerced to opts[0] on the next save.
  if (f.t === 'select') { const cur = value || f.opts[0]; const known = f.opts.includes(cur); return <Lbl t={label}><select className={inp} value={cur} onChange={(e) => onChange(e.target.value)}>{f.opts.map((o) => <option key={o}>{o}</option>)}{!known && <option value={cur}>{cur}</option>}</select></Lbl>; }
  if (f.t === 'textarea') return <Lbl t={label}><textarea className={inp} rows={2} value={value || ''} placeholder={f.ph} onChange={(e) => onChange(e.target.value)} /></Lbl>;
  // OPSLIB-04: newline-delimited (not comma) — examples/aliases/negativeExamples/triggerAttributes are free
  // text that legitimately contains commas ("Doe, John"); a comma split silently tore one value into two.
  if (f.t === 'list') return <Lbl t={`${label} (one per line)`}><textarea className={inp} rows={2} value={(value || []).join('\n')} placeholder={f.ph} onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} /></Lbl>;
  if (f.t === 'map') return <MapField label={label} value={value} onChange={onChange} />;
  if (f.t === 'password') return <SecretInput label={label} value={value} ph={f.ph} onChange={onChange} />;
  return <Lbl t={label}><input className={inp} value={value || ''} placeholder={f.ph} onChange={(e) => onChange(e.target.value)} /></Lbl>;
}

// BUG-03: credential fields render masked (type=password) with a Show/Hide toggle. The value loaded from the
// server is already masked (••••last4) — editing it replaces the secret; leaving it saves the mask, which the
// backend treats as "unchanged" (keeps the stored secret). A literal value nudges the author toward ${secret:}.
function SecretInput({ label, value, ph, onChange }) {
  const [show, setShow] = useState(false);
  const v = value || '';
  const isRef = v.trim().startsWith('${') || v.trim().startsWith('env:');
  const isMasked = v.startsWith('••••');
  const warnLiteral = v && !isRef && !isMasked;
  return (
    <Lbl t={label}>
      <div className="flex gap-1.5">
        <input className={inp} type={show ? 'text' : 'password'} value={v} placeholder={ph}
          autoComplete="off" onChange={(e) => onChange(e.target.value)} />
        <Button size="sm" variant="outline" onClick={() => setShow((s) => !s)}>{show ? 'Hide' : 'Show'}</Button>
      </div>
      {warnLiteral && <div className="text-[11px] text-amber-600 mt-1">Tip: reference a stored secret with <code>{'$'}{'{secret:NAME}'}</code> instead of a literal value.</div>}
    </Lbl>
  );
}

// OPSLIB-03: RFC-4180-ish CSV so a cell containing a comma/quote (e.g. "Paris, Texas") survives the round-trip
// instead of being torn into extra columns. Line-level parser (handles quoted commas within a line).
const csvCell = (s) => { const v = s == null ? '' : String(s); return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v; };
export const csvRow = (arr) => (arr || []).map(csvCell).join(',');
export const parseCsvLine = (line) => {
  const out = []; let cur = ''; let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
    else if (ch === '"') q = true;
    else if (ch === ',') { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur); return out.map((s) => s.trim());
};

function DomainEditor({ draft, setDraft, authSession, onImported, onError }) {
  const cols = (draft.columns || []).map((c) => c.name).join(', ');
  const rowsCsv = (draft.rows || []).map((r) => csvRow(r)).join('\n');
  const importCsv = async (file) => {
    if (!file) return;
    if (!draft.id?.trim()) { onError?.('Set an id first, then import a CSV.'); return; }
    // CSV-IMPORT-SWALLOWED: a failed import used to be dropped entirely (the "surfaced via list reload"
    // comment was wrong — onImported only ran on success), so the author saw NOTHING on a parse/size/auth
    // error. Route the failure into the page's error banner instead. file.text() is inside the try too.
    try {
      const text = await file.text();
      const t = await importDomainTableCsv(draft.id, text, draft.name, authSession);
      setDraft({ ...draft, ...t, __new: false }); onImported?.();
    } catch (e) { onError?.(`CSV import failed: ${e.message}`); }
  };
  return (
    <div>
      <Lbl t="name"><input className={inp} value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Lbl>
      {/* OPSLIB-01: preserve each existing column's type/metadata on edit — re-use the prior column object by
          name instead of hardcoding {type:'STRING'}, which silently reset every column's type on a name edit. */}
      <Lbl t="columns (comma)"><input className={inp} value={cols} placeholder="name,country" onChange={(e) => setDraft({ ...draft, columns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map((n) => (draft.columns || []).find((c) => c.name === n) || { name: n, type: 'STRING' }) })} /></Lbl>
      <Lbl t="key columns (composite key for COMBINATION)"><input className={inp} value={(draft.keyColumns || []).join(', ')} placeholder="source,destination" onChange={(e) => setDraft({ ...draft, keyColumns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} /></Lbl>
      <Lbl t="rows (CSV, one per line)"><textarea className={inp} rows={6} value={rowsCsv} placeholder={'Paris,France\nMumbai,India\n"Paris, Texas",USA'} onChange={(e) => setDraft({ ...draft, rows: e.target.value.split('\n').filter((l) => l.trim()).map((l) => parseCsvLine(l)) })} /></Lbl>
      <label className="text-xs text-blue-600 cursor-pointer">⬆ Import CSV (header row → columns)<input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files[0])} /></label>
    </div>
  );
}

// Code capabilities: admin-authored sandboxed functions exposed to flows (run out-of-process on the
// Lambda executor). This panel lists them, shows each one's typed contract + source (read-only), and lets
// you test-invoke the published version with JSON input — so you can see the result/price a flow would get.
const CC_TEMPLATE = `async function main(input, ctx) {
  // ctx surface:
  //   ctx.query(table, filter)     -> the FIRST matching row (object) or null. Unfiltered = row 1. (alias: ctx.queryOne)
  //   ctx.queryAll(table, filter)  -> ALL matching rows as an array ([] when none). Use this for "if (rows.length)".
  //   ctx.call(connection, {...})  -> call a declared connection/API; returns the parsed JSON.
  //   ctx.secrets.yourSecretKey    -> an injected declared secret (also ctx.secret('yourSecretKey')).
  //   ctx.log(message, data)       -> a redacted line in the capability trace. ctx.tenantId is your tenant id.
  const rows = await ctx.queryAll('your-table', { key: input.someKey });
  if (!rows.length) throw new Error('no match');   // ctx.query would silently give row 1 for an unfiltered read
  // Compute + return your declared outputs:
  return { result: rows[0] };
}`;

// Recipes — pre-filled patterns so admins don't start from a blank editor. Each fills the contract, refs,
// and a working async body; the admin renames the table/connection and tweaks. (The AI-assist path few-shots
// from these same patterns.) 'refs' hints are placeholders the admin swaps for their own library items.
const RECIPES = [
  {
    id: 'blank', name: 'Blank', desc: 'Start from scratch.',
    template: { inputs: [{ key: 'input1', type: 'string', required: true }], outputs: [{ key: 'result', type: 'number', required: true }], connectionRefs: [], dataRefs: [], secretKeys: [], sourceCode: CC_TEMPLATE },
  },
  {
    id: 'normalize', name: 'Normalize via table', desc: 'Map a value to its canonical form from a table (e.g. Illinois → IL).',
    template: {
      inputs: [{ key: 'value', type: 'string', required: true }],
      outputs: [{ key: 'normalized', type: 'string', required: true }, { key: 'ok', type: 'boolean', required: false }],
      connectionRefs: [], dataRefs: ['your-table'], secretKeys: [],
      sourceCode: `async function main(input, ctx) {
  const row = await ctx.query('your-table', { name: input.value });
  return { normalized: row ? row.code : input.value, ok: !!row };
}`,
    },
  },
  {
    id: 'lookup', name: 'Lookup & enrich', desc: 'Look up a row by key and return several columns.',
    template: {
      inputs: [{ key: 'key', type: 'string', required: true }],
      outputs: [{ key: 'field1', type: 'string', required: false }, { key: 'field2', type: 'string', required: false }],
      connectionRefs: [], dataRefs: ['your-table'], secretKeys: [],
      sourceCode: `async function main(input, ctx) {
  const row = await ctx.query('your-table', { id: input.key });
  if (!row) return { field1: '', field2: '' };
  return { field1: row.field1, field2: row.field2 };
}`,
    },
  },
  {
    id: 'price', name: 'Price by distance', desc: 'Look up rate + distance and compute a price.',
    template: {
      inputs: [{ key: 'pickup', type: 'string', required: true }, { key: 'dropoff', type: 'string', required: true }, { key: 'vehicle', type: 'string', required: false }],
      outputs: [{ key: 'price', type: 'number', required: true }, { key: 'miles', type: 'number', required: false }],
      connectionRefs: [], dataRefs: ['distances', 'rates'], secretKeys: [],
      sourceCode: `async function main(input, ctx) {
  const d = await ctx.query('distances', { from: input.pickup, to: input.dropoff });
  const r = await ctx.query('rates', { vehicle: input.vehicle || 'default' });
  if (!d || !r) return { price: 0, miles: 0 };
  return { price: Math.round((r.base + d.miles * r.perMile) * 100) / 100, miles: d.miles };
}`,
    },
  },
  {
    id: 'external', name: 'Call external API', desc: 'Call a connection (GET) and map response fields.',
    template: {
      inputs: [{ key: 'id', type: 'string', required: true }],
      outputs: [{ key: 'name', type: 'string', required: false }, { key: 'ok', type: 'boolean', required: false }],
      connectionRefs: ['your-connection'], dataRefs: [], secretKeys: [],
      sourceCode: `async function main(input, ctx) {
  const res = await ctx.call('your-connection', { method: 'GET', path: '/' + input.id });
  return { name: res && res.name ? res.name : '', ok: !!(res && res.name) };
}`,
    },
  },
  {
    id: 'validate', name: 'Validate against list', desc: 'Check a value exists in a table; return ok + reason.',
    template: {
      inputs: [{ key: 'value', type: 'string', required: true }],
      outputs: [{ key: 'ok', type: 'boolean', required: true }, { key: 'reason', type: 'string', required: false }],
      connectionRefs: [], dataRefs: ['allowed-values'], secretKeys: [],
      sourceCode: `async function main(input, ctx) {
  const row = await ctx.query('allowed-values', { name: input.value });
  return row ? { ok: true, reason: '' } : { ok: false, reason: input.value + ' is not supported.' };
}`,
    },
  },
];

const sampleFor = (t) => ({ number: 0, boolean: false, array: [], object: {} }[t] ?? '');
const sampleInput = (cap) => JSON.stringify(Object.fromEntries((cap.inputs || []).map((f) => [f.key, sampleFor(f.type)])), null, 2);

// Editable rows of {key, type, required} — the typed input/output contract.
function IoRows({ label, rows, onChange }) {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-slate-500">{label}</span>
        <button className="text-[11px] text-blue-600" onClick={() => onChange([...rows, { key: '', type: 'string', required: false }])}>+ add</button>
      </div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-1.5 items-center">
            <input className={inp} placeholder="key" value={r.key} onChange={(e) => set(i, { key: e.target.value })} />
            <select className={`${inp} w-24`} value={r.type} onChange={(e) => set(i, { type: e.target.value })}>
              {['string', 'number', 'boolean', 'object', 'array'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="text-[11px] text-slate-500 flex items-center gap-1"><input type="checkbox" checked={!!r.required} onChange={(e) => set(i, { required: e.target.checked })} />req</label>
            <button className="text-rose-500 text-xs px-1" onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CsvField({ label, hint, value, onChange }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-0.5">{label} <span className="text-slate-300">{hint}</span></div>
      <input className={inp} value={(value || []).join(', ')}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
    </div>
  );
}

// Pick refs as toggle chips from what actually exists in the library (no typos).
function MultiPick({ label, hint, options, value, onChange }) {
  const val = value || [];
  const toggle = (id) => onChange(val.includes(id) ? val.filter((v) => v !== id) : [...val, id]);
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1">{label} <span className="text-slate-300">{hint}</span></div>
      <div className="flex flex-wrap gap-1">
        {options.length === 0 && <span className="text-[11px] text-slate-300">none in library yet</span>}
        {options.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)}
            className={`text-[11px] px-2 py-0.5 rounded-full border ${val.includes(o) ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}

// Admin authoring surface: create / edit / test-run / publish code capabilities, and mark them premium.
function CodeCapabilitiesPanel({ authSession, isAdmin }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [draft, setDraft] = useState(null);
  const [input, setInput] = useState('{}');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false); // recipe picker open
  const [aiPrompt, setAiPrompt] = useState('');
  const [marks, setMarks] = useState([]);
  const [connOpts, setConnOpts] = useState([]);
  const [dataOpts, setDataOpts] = useState([]);
  const [refOptsError, setRefOptsError] = useState(null); // CCP-REFOPTS-SWALLOWED
  const openSeq = useRef(0); // CCP-OPEN-LOAD-RACE

  const reload = useCallback(() => {
    listCodeCapabilities(authSession).then((r) => setItems(r || [])).catch((e) => setError(e.message));
    getPremiumMarks(authSession).then((d) => setMarks(d?.premium || [])).catch(() => {});
  }, [authSession]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    // CCP-REFOPTS-SWALLOWED: a swallowed failure here left the Connections / Data-table pickers EMPTY, so an
    // author editing a code capability was silently told there are "none in library yet" and couldn't attach
    // the refs their code needs. Surface the failure so the empty pickers are explained.
    setRefOptsError(null);
    connections.list(authSession).then((r) => setConnOpts((r || []).map((c) => c.id))).catch((e) => setRefOptsError(e?.message || 'Could not load Connections / Data tables.'));
    domainTables.list(authSession).then((r) => setDataOpts((r || []).map((t) => t.id))).catch((e) => setRefOptsError(e?.message || 'Could not load Connections / Data tables.'));
  }, [authSession]);

  const open = (id) => {
    // CCP-OPEN-LOAD-RACE: monotonic token so a slower getCodeCapability for a previously-clicked item can't
    // land last and overwrite the capability the admin actually selected.
    const seq = ++openSeq.current;
    setError(null); setResult(null);
    getCodeCapability(id, authSession).then((c) => { if (seq === openSeq.current) { setSel(c); setInput(sampleInput(c)); setMode('view'); } }).catch((e) => { if (seq === openSeq.current) setError(e.message); });
  };
  const blank = () => ({
    id: '', name: '', scope: 'TENANT', runtime: 'js',
    inputs: [{ key: 'input1', type: 'string', required: true }],
    outputs: [{ key: 'result', type: 'number', required: true }],
    connectionRefs: [], dataRefs: [], secretKeys: [],
    limits: { timeoutMs: 5000, memoryMb: 256, maxInstructions: 5000000 }, sourceCode: CC_TEMPLATE, publishedVersion: 0,
  });
  const startFromRecipe = (r) => {
    const d = { ...blank(), ...r.template, id: '', name: r.id === 'blank' ? '' : r.name };
    setDraft(d); setSel(d); setResult(null); setError(null); setPicking(false); setMode('edit');
  };
  const generateWithAi = async () => {
    if (!aiPrompt.trim()) return;
    setBusy(true); setError(null);
    try {
      const gen = await assistCodeCapability(aiPrompt, authSession);
      const d = { ...blank(), ...gen, id: '' };
      setDraft(d); setSel(d); setResult(null); setPicking(false); setMode('edit');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(sel))); setResult(null); setMode('edit'); };
  const cancel = () => { if (sel && !items.some((i) => i.id === sel.id)) setSel(null); setMode('view'); };

  const persist = async () => {
    const exists = items.some((i) => i.id === draft.id);
    return exists ? saveCodeCapability(draft.id, draft, authSession) : createCodeCapability(draft, authSession);
  };
  const save = async () => {
    if (!draft.id?.trim()) { setError('id is required'); return; }
    setBusy(true); setError(null);
    try { const saved = await persist(); await reload(); setSel(saved); setInput(sampleInput(saved)); setMode('view'); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true); setError(null);
    try { await publishCodeCapability(sel.id, authSession); await reload(); open(sel.id); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete code capability "${sel.id}"? This can't be undone.`)) return;
    setBusy(true); setError(null);
    try { await deleteCodeCapability(sel.id, authSession); await reload(); setSel(null); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const parsed = JSON.parse(input || '{}');
      if (mode === 'edit') {
        if (!draft.id?.trim()) { setError('id is required'); return; }
        const saved = await persist(); await reload();
        setResult(await testCodeCapability(saved.id, parsed, authSession));
      } else {
        setResult(await invokeCodeCapability(sel.id, parsed, authSession));
      }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const togglePremium = async (id, next) => {
    const ref = `codeCapability:${id}`;
    setMarks((m) => (next ? [...m, ref] : m.filter((r) => r !== ref)));
    try { await setPremiumMark('codeCapability', id, next, authSession); } catch (e) { setError(e.message); reload(); }
  };
  const isPremium = (id) => marks.includes(`codeCapability:${id}`);
  const D = (patch) => setDraft({ ...draft, ...patch });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 max-w-2xl">
          Sandboxed functions a flow calls as a typed step — read your tables (ctx.query = first row, ctx.queryAll =
          all rows), call connections (ctx.call), use injected secrets (ctx.secrets), trace (ctx.log). Runs
          out-of-process. Author, test, then publish a version.
        </div>
        {isAdmin && <Button size="sm" onClick={() => { setPicking(true); setSel(null); setMode('view'); setError(null); }}>+ New</Button>}
      </div>
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <div className="space-y-1">
          {items.length === 0 && <div className="text-sm text-slate-400">No code capabilities yet.</div>}
          {items.map((c) => (
            <button key={c.id} onClick={() => open(c.id)}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${sel?.id === c.id ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
              <div className="font-medium text-slate-800 flex items-center gap-1">{isPremium(c.id) && <span title="premium">⭐</span>}{c.name}</div>
              <div className="text-[11px] text-slate-500 font-mono">{c.id} · {c.scope?.toLowerCase()} · v{c.publishedVersion || 0}</div>
            </button>
          ))}
        </div>

        <div>
          {picking && (
            <div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 mb-3">
                <div className="text-[11px] font-semibold text-indigo-600 mb-1">✨ Describe it — AI drafts the contract + code</div>
                <textarea className={inp} rows={2}
                  placeholder="e.g. resolve a US zip to city and state using the zip-resolver connection"
                  value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} />
                <Button size="sm" className="mt-2" onClick={generateWithAi} disabled={busy || !aiPrompt.trim()}>{busy ? 'Generating…' : 'Generate draft'}</Button>
                <span className="text-[11px] text-slate-400 ml-2">You review, test &amp; publish — nothing goes live automatically.</span>
              </div>
              <div className="text-sm font-semibold text-slate-700 mb-2">…or start from a recipe</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RECIPES.map((r) => (
                  <button key={r.id} onClick={() => startFromRecipe(r)}
                    className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-400 hover:bg-blue-50">
                    <div className="text-sm font-medium text-slate-800">{r.name}</div>
                    <div className="text-[11px] text-slate-500">{r.desc}</div>
                  </button>
                ))}
              </div>
              <button className="text-[11px] text-slate-400 hover:text-slate-600 mt-2" onClick={() => setPicking(false)}>cancel</button>
            </div>
          )}
          {!picking && !sel && <div className="text-sm text-slate-400">Select a capability, or {isAdmin ? '+ New to author one.' : 'ask an admin to author one.'}</div>}

          {sel && mode === 'view' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-800">{sel.name} <span className="text-[11px] font-mono text-slate-400">v{sel.publishedVersion || 0}</span></div>
                <div className="flex gap-2 items-center">
                  {isAdmin && <label className="text-[11px] text-slate-500 flex items-center gap-1"><input type="checkbox" checked={isPremium(sel.id)} onChange={(e) => togglePremium(sel.id, e.target.checked)} />premium</label>}
                  {isAdmin && <Button size="sm" variant="outline" onClick={startEdit}>Edit</Button>}
                  {isAdmin && <Button size="sm" onClick={publish} disabled={busy}>Publish</Button>}
                  {isAdmin && <button className="text-rose-500 text-xs hover:text-rose-700" onClick={del} disabled={busy}>Delete</button>}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1">
                <div><span className="text-slate-400">in</span> {(sel.inputs || []).map((f) => `${f.key}: ${f.type}${f.required ? '' : '?'}`).join(', ') || '—'}</div>
                <div><span className="text-slate-400">out</span> {(sel.outputs || []).map((f) => `${f.key}: ${f.type}`).join(', ') || '—'}</div>
                <div><span className="text-slate-400">connections</span> {(sel.connectionRefs || []).join(', ') || '—'} · <span className="text-slate-400">data</span> {(sel.dataRefs || []).join(', ') || '—'} · <span className="text-slate-400">secrets</span> {(sel.secretKeys || []).join(', ') || '—'}</div>
              </div>
              <pre className="text-[11px] bg-slate-900 text-slate-100 rounded p-2 overflow-auto whitespace-pre-wrap max-h-52">{sel.sourceCode || '(empty)'}</pre>
              <TestBox input={input} setInput={setInput} run={run} busy={busy} disabled={!sel.publishedVersion} label="Invoke (published)"
                note={!sel.publishedVersion ? 'Not published yet — publish a version to invoke.' : null} result={result} />
            </div>
          )}

          {mode === 'edit' && draft && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input className={inp} placeholder="id (slug)" value={draft.id} disabled={items.some((i) => i.id === draft.id)}
                  onChange={(e) => D({ id: e.target.value.trim() })} />
                <input className={inp} placeholder="name" value={draft.name} onChange={(e) => D({ name: e.target.value })} />
                <select className={`${inp} w-32`} value={draft.scope} onChange={(e) => D({ scope: e.target.value })}>
                  <option value="TENANT">Tenant</option><option value="PLATFORM">Platform</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <IoRows label="Inputs" rows={draft.inputs} onChange={(r) => D({ inputs: r })} />
                <IoRows label="Outputs" rows={draft.outputs} onChange={(r) => D({ outputs: r })} />
              </div>
              {refOptsError && <div className="text-[11px] text-amber-600">⚠ {refOptsError} The Connection / Data-table pickers below may be incomplete — retry.</div>}
              <MultiPick label="Connections" hint="ctx.call refs" options={connOpts} value={draft.connectionRefs} onChange={(v) => D({ connectionRefs: v })} />
              <MultiPick label="Data tables" hint="ctx.query refs" options={dataOpts} value={draft.dataRefs} onChange={(v) => D({ dataRefs: v })} />
              <CsvField label="Secret keys" hint="ctx.secrets.*" value={draft.secretKeys} onChange={(v) => D({ secretKeys: v })} />
              <div>
                <div className="text-[11px] text-slate-500 mb-0.5">Source — <span className="font-mono">async function main(input, ctx)</span> (use <span className="font-mono">await ctx.query/ctx.call</span>)</div>
                <textarea className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-mono" rows={12}
                  value={draft.sourceCode} onChange={(e) => D({ sourceCode: e.target.value })} spellCheck={false} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={save} disabled={busy}>Save draft</Button>
                <Button size="sm" variant="outline" onClick={cancel}>Cancel</Button>
              </div>
              <TestBox input={input} setInput={setInput} run={run} busy={busy} disabled={false} label="Save & test draft" result={result} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TestBox({ input, setInput, run, busy, disabled, label, note, result }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] text-slate-500">Test input (JSON)</div>
        <Button size="sm" onClick={run} disabled={busy || disabled}>{busy ? 'Running…' : label}</Button>
      </div>
      <textarea className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-mono" rows={6}
        value={input} onChange={(e) => setInput(e.target.value)} spellCheck={false} />
      {note && <div className="text-[11px] text-amber-700 mt-1">{note}</div>}
      {result && (
        <pre className="text-[11px] bg-emerald-950 text-emerald-100 rounded p-2 overflow-auto whitespace-pre-wrap max-h-52 mt-2">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

export default OpsLibrary;
